/**
 * Account service — client-side orchestration for the Delete Account flow (M72 Phase 3).
 *
 * Sequence on deleteAccount():
 *   1. Offline guard — abort before any network call if offline (mirror billing-service.ts pattern)
 *   2. Invoke the 'delete-account' Edge Function with EMPTY body (mirror billing-service.ts:22).
 *      No userId in body — the function derives it from the JWT subject.
 *   3. If the Edge Function returns an error OR a non-success status:
 *      THROW before any local mutation. Dexie and all Zustand stores stay intact.
 *   4. Only on full cloud success:
 *      a. await db.delete()         — wipe all local IndexedDB data
 *      b. await authService.signOut() — fires the existing onAuthStateChange SIGNED_OUT
 *         handler in auth-store.ts which resets auth-store, posthog.reset(), sentry.setUser(null).
 *         Note: this signOut() call is NEW — invoked after db.delete(), not a reuse of
 *         the Sign Out UI path (which is gated behind showSignOutDialog in ProfilePage).
 *   5. Navigate to '/auth' — caller (ProfilePage) holds the useNavigate reference.
 *
 * Navigation is passed in by the caller so this service stays framework-agnostic
 * and testable without a router context.
 */

import { supabase } from '@/services/supabase';
import { db } from '@/services/storage/db';
import { authService } from '@/services/auth-service';

// ── Types ─────────────────────────────────────────────────────────────────────

export class DeleteAccountOfflineError extends Error {
  constructor() {
    super('An internet connection is required to delete your account.');
    this.name = 'DeleteAccountOfflineError';
  }
}

export class DeleteAccountCloudError extends Error {
  readonly stage?: string;

  constructor(message: string, stage?: string) {
    super(message);
    this.name = 'DeleteAccountCloudError';
    this.stage = stage;
  }
}

interface DeleteAccountSuccessResponse {
  status: 'success';
}

interface DeleteAccountErrorResponse {
  status: 'error';
  stage?: string;
  message?: string;
}

type DeleteAccountResponse = DeleteAccountSuccessResponse | DeleteAccountErrorResponse;

// ── Service ───────────────────────────────────────────────────────────────────

/**
 * Permanently deletes the authenticated user's account.
 *
 * @param navigate - React Router navigate function. Called with '/auth' only on success.
 *   The caller (ProfilePage) owns the navigate reference.
 *
 * @throws {DeleteAccountOfflineError} if the device is offline before any cloud call
 * @throws {DeleteAccountCloudError} if the Edge Function returns an error — local data is NOT wiped
 */
export async function deleteAccount(navigate: (path: string) => void): Promise<void> {
  // 1. Offline guard — mirror billing-service.ts pattern
  if (!navigator.onLine) {
    throw new DeleteAccountOfflineError();
  }

  // 2. Invoke Edge Function with EMPTY body. supabase-js attaches the Authorization
  //    header automatically from the cached session. No userId in body — the
  //    function derives it from the JWT subject (security gate).
  const { data, error } = await supabase.functions.invoke('delete-account', {});

  // 3. Abort before any local mutation if cloud call failed.
  if (error) {
    throw new DeleteAccountCloudError(
      error.message ?? 'Account deletion failed. Please try again.',
    );
  }

  const responseData = data as DeleteAccountResponse | null;
  if (!responseData || responseData.status !== 'success') {
    const errorResponse = responseData as DeleteAccountErrorResponse | null;
    throw new DeleteAccountCloudError(
      errorResponse?.message ?? 'Account deletion failed. Please try again.',
      errorResponse?.stage,
    );
  }

  // 4. Cloud deletion succeeded — wipe local data.
  //    db.delete() drops the entire IndexedDB database including all user data.
  await db.delete();

  //    authService.signOut() fires the SIGNED_OUT event in onAuthStateChange
  //    (auth-store.ts) which resets auth-store state, calls posthog.reset(),
  //    and calls sentry.setUser(null). This is a NEW signOut() invocation — not
  //    the same as the Sign Out UI path which is gated behind showSignOutDialog.
  await authService.signOut();

  // 5. Navigate to /auth. Called after local wipe — no going back.
  navigate('/auth');
}
